/**
 * =============================================================================
 *  LUAT BAN PHIM VA CUA CHAN CUA O SOAN - TACH RA THANH HAM THUAN TUY
 * =============================================================================
 *
 *  Hai cho soan chu (chu san co trong tai lieu, va hop chu / ghi chu moi) gio
 *  dung CHUNG mot vo soan (ui/TextEditorShell.tsx). Cai gi thuoc ve HANH VI cua
 *  vo do - phim nao lam gi, khi nao duoc phep xac nhan - nam o day, khong nam
 *  trong ma React.
 *
 *  Ly do rat cu the: bo test cua du an chay bang node:test, khong co DOM va
 *  khong dung duoc thu vien dung thu React. Neu luat ban phim chi ton tai ben
 *  trong mot onKeyDown thi khong mot phep kiem nao cham toi no duoc, va "Enter
 *  de luu" se lang le hong dung nhu no da tung hong. O day thi kiem duoc tung
 *  duong mot.
 */
import type { TextAlign } from './textEditCore';

/** Phim tat can le: Ctrl+Shift+L / E / R - dung bo phim cua Word va Google Docs. */
const ALIGN_KEYS: Record<string, TextAlign> = { l: 'left', e: 'center', r: 'right' };

/** Phan cua mot su kien ban phim ma luat nay can. Khop voi React.KeyboardEvent. */
export interface EditorKeyEvent {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

/**
 * Cai o soan HIEN TAI cho phep lam gi.
 *
 * Cau thang pham vi (O / Dong / Ca khoi) da bi go bo theo yeu cau nguoi dung:
 * moi lan sua luon nham dung mot run (o) duoi con tro. Nen khong con phim
 * Alt+Mui ten noi rong / thu hep nua - xem core/edit/scope.ts van giu phan giai
 * cap run lam hanh vi co dinh, chi UI chon pham vi la mat di.
 */
export interface EditorKeyCaps {
  alignSupported: boolean;
}

export type EditorKeyAction =
  /** Khong phai phim cua o soan - de trinh duyet go binh thuong. */
  | { kind: 'none' }
  | { kind: 'commit' }
  /** Xuong dong: cung la "de yen cho o soan lam viec cua no". */
  | { kind: 'newline' }
  | { kind: 'cancel' }
  | { kind: 'align'; align: TextAlign };

/**
 * Dich mot phim thanh thao tac cua o soan.
 *
 * MOI thao tac o day deu PHAI duoc xu ly ngay trong o soan chu khong o bang
 * phim tat toan cuc: bang phim tat toan cuc bo qua moi su kien phat ra tu
 * INPUT/TEXTAREA (co y - neu khong thi go chu "b" se bat dam). Nen khi con tro
 * dang nam trong o soan, day la CHO DUY NHAT nhin thay nhung phim nay.
 *
 * Ctrl+Shift+chu cai (can le) khong cuop phim di chuyen / boi den cua o soan.
 */
export function editorKeyAction(e: EditorKeyEvent, caps: EditorKeyCaps): EditorKeyAction {
  if (e.key === 'Escape') return { kind: 'cancel' };

  // Enter = xac nhan. Shift+Enter = xuong dong. Doan van nhieu dong van go duoc,
  // ma thao tac thuong gap nhat van chi mat mot phim.
  //
  // Ctrl+Enter cung xac nhan: o chu giai cua ghi chu truoc day chi nhan bo phim
  // do, va nguoi dung da quen tay. Giu lai khong ton gi va khong dam vao dau.
  if (e.key === 'Enter') {
    if (e.shiftKey) return { kind: 'newline' };
    return { kind: 'commit' };
  }

  if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey) {
    const align = ALIGN_KEYS[e.key.toLowerCase()];
    // Loi khong nhan tuy chon can le thi phim nay KHONG duoc coi la da xu ly:
    // bao mot thao tac ma tep xuat ra se khong co dung la kieu "thanh cong gia"
    // ma ca duong sua chu nay duoc viet ra de chan.
    if (align && caps.alignSupported) return { kind: 'align', align };
    return { kind: 'none' };
  }

  return { kind: 'none' };
}

/**
 * =============================================================================
 *  CUA SO TUOI MOI CUA BAO CAO CHAY THU
 * =============================================================================
 *
 *  Mot bao cao chay thu chi co gia tri cho DUNG BO SAU (van ban, can le, dam,
 *  nghieng, PHONG CHU, CO CHU) ma no duoc tinh. Doi bat ky truong nao trong sau
 *  truong do la bao cao cu HET HAN:
 *
 *    - van ban: hien nhien.
 *    - can le : so dong khong doi nhung TOA DO X cua tung dong doi, ma toa do x
 *               la thu duong ghi lay thang tu bao cao.
 *    - dam / nghieng: font dam RONG HON font thuong, nen mot doan vua khoi khi
 *               de thuong co the khong vua khi in dam. Ket luan "vua khối" cua
 *               mat chu nay khong dung cho mat chu kia.
 *    - phong chu: moi ho font co so do rieng - be rong glyph khac, dau tieng
 *               Viet chong tang o do cao khac (bai hoc so 4). Mot doan vua khoi
 *               o Noto Sans co the KHONG vua o Noto Serif. Bao cao tinh cho phong
 *               A khong duoc phep xac nhan sau khi nguoi dung doi sang phong B.
 *
 *  Dua vao rieng co `pending` la KHONG DU: effect chay thu khoi dong sau khi
 *  React ve, nen co dung mot khung hinh mang bao cao cu voi pending=false - va
 *  trong khung hinh do nut xac nhan sang len sai.
 */
export interface PreflightTuple {
  text: string;
  align: TextAlign | null;
  bold: boolean;
  italic: boolean;
  /** Ma ho font ma bao cao duoc tinh cho, hoac null = phong mac dinh. */
  family: string | null;
  /**
   * Co chu ma bao cao duoc tinh cho, hoac null = co chu do duoc tu chu cu.
   *
   * Cung cua so tuoi moi voi cac truong tren: doi co chu doi so dong va toa do
   * dat chu (ADR-005: co chu lon co the bi thu nho de vua khoi). Xac nhan bang
   * bao cao tinh cho co chu cu la ghi ra mot bo cuc khac voi bo cuc da xem truoc.
   */
  size: number | null;
}

export function preflightIsFresh(computedFor: PreflightTuple, current: PreflightTuple): boolean {
  return (
    computedFor.text === current.text &&
    computedFor.align === current.align &&
    computedFor.bold === current.bold &&
    computedFor.italic === current.italic &&
    computedFor.family === current.family &&
    computedFor.size === current.size
  );
}
